import { Injectable, Output, EventEmitter } from "@angular/core";
import { MatDialog } from "@angular/material";

import { WizardDialogService } from "app/administrative/wizard-dialog/wizard-dialog.service";
import { GatewayEditNameComponent } from "./gateway-edit-name/gateway-edit-name.component";
import { GatewayView } from "../gateway-view";
import { GatewayUpdate, GatewaysService, IpAndPortsWithUuid } from "../gateways.service";
import { PrivateGateway, Port, Gateway } from "@impulse/sdp-shared-models";
import * as gatewayIcon from "app/../assets/svg/gatewayDashboardIcon.svg";
import { SdpIcon } from "app/common/icon/models";
import { AbstractControl, FormGroup, ValidationErrors } from "@angular/forms";
import { SnackbarService } from "app/common/snackbar/snackbar.service";
import { TranslateService } from "app/common/translate";
import { GatewayEditIpPortsComponent } from "./gateway-edit-ip-ports/gateway-edit-ip-ports.component";

interface PrivateGatewayLabel {
	label: PrivateGateway.Label;
}

interface PrivateGatewayIpPorts {
	publicOpenVpnPort: Port.PortNumber;
	publicFwknopPort: Port.PortNumber;
	publicIp: Gateway.Host;
	privateIp: Gateway.Host;
}

interface PrivateGatewayDns {
	dnsServer1: Gateway.Host;
	dnsServer2: Gateway.Host;
	dnsEnabled: PrivateGateway.DnsConfig.Enabled;
	dnsSuffixEnabled: PrivateGateway.DnsConfig.Enabled;
	dnsSuffix: PrivateGateway.DnsConfig.Suffix;
}

export interface PrivateGatewayEdit {
	editGateway: GatewayView;
	allGateways: GatewayView[];
	activateWhenUpdated: boolean;
}

@Injectable()
export class GatewayEditWizardService {
	public uuid!: string;
	private privateGatewayEdit?: PrivateGatewayEdit;

	private readonly gatewayIcon: SdpIcon.ImageIcon = {
		type: "image",
		name: "gateway",
		src: gatewayIcon,
		cssClass: "sdp-gateway-edit-title-icon-offset sdp-dashboard-img-icon"
	};

	@Output() public submitSuccessEvent: EventEmitter<string> = new EventEmitter(true);

	constructor(
		public dialog: MatDialog,
		private wizardDialogService: WizardDialogService,
		private gatewayService: GatewaysService,
		public snackbarService: SnackbarService,
		public translateService: TranslateService
	) {

	}

	public createDialog(gateway: GatewayView, activateWhenUpdated: boolean) {
		this.uuid = gateway.uuid;
		this.privateGatewayEdit = {
			editGateway: gateway,
			allGateways: this.gatewayService.gatewayViews,
			activateWhenUpdated: activateWhenUpdated
		};
		this.wizardDialogService.openDialog(
			activateWhenUpdated ? GatewayEditIpPortsComponent : GatewayEditNameComponent,
			this.gatewayIcon,
			false,
			this.privateGatewayEdit
		);
	}

	public async processGateway() {
		this.dialog.closeAll();
		const edit = this.assertData();
		await this.gatewayService.editGateway(this.uuid, this.gatewayViewToGatewayUpdate(edit.editGateway));
		if (edit.activateWhenUpdated) {
			const result = await this.gatewayService.activateGateway(this.uuid);
			if (!!result) {
				this.snackbarService.open(this.translateService.translate(
					"sdp.gateway.activated",
					{
						label: edit.editGateway.label
					}
				));
			}
		}
	}

	public processName(values: PrivateGatewayLabel) {
		const edit = this.assertData();
		edit.editGateway.label = values.label;
	}

	public processIpPorts(values: PrivateGatewayIpPorts) {
		const edit = this.assertData();
		const publicIpPorts = this.gatewayService.getPublicHostAndPorts(edit.editGateway.hostAndPorts);
		publicIpPorts.host = values.publicIp;
		this.gatewayService.getPrivateHostAndPorts(edit.editGateway.hostAndPorts).host = values.privateIp;
		publicIpPorts.openvpnPort = Number(values.publicOpenVpnPort);
		publicIpPorts.fwknopPort = Number(values.publicFwknopPort);

	}

	public processDns(values: PrivateGatewayDns) {
		const edit = this.assertData();
		edit.editGateway.dnsEnabled = values.dnsEnabled;
		edit.editGateway.dnsServer1 = values.dnsServer1;
		edit.editGateway.dnsServer2 = values.dnsServer2;
		edit.editGateway.dnsSuffixEnabled = values.dnsSuffixEnabled;
		if (values.dnsSuffixEnabled) {
			edit.editGateway.dnsSuffix = values.dnsSuffix;
		} else {
			edit.editGateway.dnsSuffix = undefined;
		}
	}

	/*
	 * compares a gateway that is being edited against active gateways
	 * if the ip's match and at least one port is the same then we fail validation
	*/
	public hasOverlappingIpPortsValidator(gateways: GatewayView[]) {
		const edit = this.assertData();
		return (formControl: FormGroup): ValidationErrors | undefined => {
			const hosts: IpAndPortsWithUuid = {
				uuid: edit.editGateway.uuid,
				host: formControl.value.publicIp,
				fwknopPort: Number.parseInt(formControl.value.publicFwknopPort, 10),
				openvpnPort: Number.parseInt(formControl.value.publicOpenVpnPort, 10)
			};

			if (this.gatewayService.hasOverlappingIpPort(hosts, gateways)) {
				return { "hasOverlappingIpPorts": true };
			}
			return undefined;
		};
	}

	private assertData() {
		if (!this.privateGatewayEdit) {
			throw new Error("privateGatewayEdit is undefined");
		}
		return this.privateGatewayEdit;
	}

	private gatewayViewToGatewayUpdate(gateway: GatewayView): GatewayUpdate {
		const morphGateway: GatewayUpdate = {
			label: gateway.label,
			publicHost: {
				ip: this.gatewayService.getPublicHostAndPorts(gateway.hostAndPorts).host,
				openVpnPort: this.gatewayService.getPublicHostAndPorts(gateway.hostAndPorts).openvpnPort,
				fwknopPort: this.gatewayService.getPublicHostAndPorts(gateway.hostAndPorts).fwknopPort,
			},
			privateHost: { ip: this.gatewayService.getPrivateHostAndPorts(gateway.hostAndPorts).host },
			dnsConfig: {
				enabled: gateway.dnsEnabled,
				hosts: processHosts(
					gateway.dnsServer1,
					gateway.dnsServer2,
					gateway.dnsEnabled
				),
				suffixEnabled: gateway.dnsSuffixEnabled,
				suffix: gateway.dnsSuffix
			}
		};
		return morphGateway;
	}
}

function processHosts(dns1: Gateway.Host, dns2: Gateway.Host, enabled: PrivateGateway.DnsConfig.Enabled) {
	const hosts: string[] = [];
	if (enabled) {
		// if custom is enabled, dns1 must be valid based on the field being required
		hosts.push(dns1);
		if (dns2.length > 0) {
			hosts.push(dns2);
		}
	}
	return hosts;
}

